import { useEffect, useState } from 'react'
import { sendGet } from '../../hooks/use-fetch'
import { Avatar, Box, Button, CircularProgress, List, ListItem, Stack, Typography } from '@mui/material'
import IdentityIcon from '@mui/icons-material/Fingerprint'
import DateRangeIcon from '@mui/icons-material/DateRange'
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined'
import CheckListIcon from '@mui/icons-material/FormatListBulleted'
import { BirthdayToAgeInYearsAndMonths } from '../../core/Utilities'
import { useRefresh } from '../../ContextProviders/RefreshProvider'
import { useAccountId } from '../../ContextProviders/CurrentAccount'
import { CARENETWORK_MANAGEMENT_DETAILS } from '../../Pages/Routes'
import { generatePath, useNavigate } from 'react-router'
import '../../core/styles.css'
import S3Avatar from '../S3Image/S3Avatar'

export interface ChildInfoCardProps {
  id: number
  showLinkToFullDetails?: boolean
  onEdit?: () => void
}

interface ChildInfoDetails {
  firstName: string
  middleName: string
  lastName: string
  nickName: string | null
  suffix: string | null
  dateOfBirth: Date | null
  dueDate: Date | null
  isUnborn: boolean
  birthGender: string
  uic: string | null
  imageFile: string | null
  childRaceList: string[]
  tribalAffiliationStatus: boolean
  tribalAffiliationName: string | null
  language: string
  authorizationStatus: string | null
  eligibilityFactorList: string[]
  environmentalRiskList: string[]
  captureIEP: boolean
  captureIFSP: boolean
  captureEOIS: boolean
  hasIEP: boolean
  hasIFSP: boolean
  hasEOIS: boolean
  hasSNAPBenefits: boolean
  isHomeless: boolean
  isFosterChild: boolean
  isOnCashAssistance: boolean
  isOnSSI: boolean
  isImmigrant: boolean
  readOnly: boolean
}

export function ChildInfoCard (props: ChildInfoCardProps): JSX.Element {
  const [details, setDetails] = useState<ChildInfoDetails | null>(null)
  const [showMoreDetails, setShowMoreDetails] = useState(false)
  const refreshTime = useRefresh()
  const account = useAccountId()
  const nav = useNavigate()

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/ChildDetails/Find?id=${props.id}&accountId=${account?.id ?? 0}&accountType=${account?.type ?? ''}&requestingModule=CareNetwork`, {})
      if (success) {
        setDetails(response.response)
      }
    }
    void loadData()
  }, [refreshTime, props.id])

  const toggleMoreDetails = (): void => {
    setShowMoreDetails(!showMoreDetails)
  }

  const redirectToDetails = (): void => {
    const editChild = `${generatePath(CARENETWORK_MANAGEMENT_DETAILS, { id: props.id.toString() })}`
    nav(editChild)
  }

  if (details === null) {
    return <CircularProgress />
  }

  const raceArray = []
  if (details.childRaceList.length > 0) {
    raceArray.push(...details.childRaceList ?? null)
  }
  if (details.tribalAffiliationStatus) {
    raceArray.push(details.tribalAffiliationName)
  }
  return <Box className='CardDisplay' data-testid="ChildInfoCard">
    <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Child Information</Typography>
    <Stack direction='column' spacing={1}>
      <Stack direction='row' spacing={1}>
        {details.imageFile !== null && details.imageFile !== ''
          ? <S3Avatar imageKey={details.imageFile} height='30px' width='30px' />
          : <Avatar sx={{ height: '30px', width: '30px' }} />}
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
            {details.middleName !== null && details.middleName !== '' ? `${details.firstName} ${details.middleName} ${details.lastName} ${details.suffix ?? ''}` : `${details.firstName} ${details.lastName} ${details.suffix ?? ''}`}
          </Typography>
          {details.nickName !== null && details.nickName !== '' &&
            <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>({details.nickName})</Typography>
          }
        </Stack>
      </Stack>
      {details.uic !== null && details.uic !== '' && <Stack direction='row' spacing={1}>
        <IdentityIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
          {details.uic}
        </Typography>
      </Stack>}
      {details.isUnborn && details.dueDate != null && details.dueDate !== new Date('01-01-0001') && (
        <Stack direction='row' spacing={1}>
          <DateRangeIcon />
          <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
            <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
              { new Date(details.dueDate).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
            {details !== null && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
              ({BirthdayToAgeInYearsAndMonths(details.dueDate, true)})
            </Typography>}
          </Stack>
        </Stack>
      )}
      {!details.isUnborn && details.dateOfBirth != null && details.dateOfBirth !== new Date('01-01-0001') && (
        <Stack direction='row' spacing={1}>
          <DateRangeIcon />
          <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
            <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
              { new Date(details?.dateOfBirth).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
            {details !== null && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
              ({BirthdayToAgeInYearsAndMonths(details.dateOfBirth, false)})
            </Typography>}
          </Stack>
        </Stack>
      )}
      <Stack direction='row' spacing={1}>
        <BallotOutlinedIcon />
        <Stack direction='column'>
          <Typography variant='body1' data-testid='birthGenderTextDisplay' sx={{ overflowWrap: 'break-word' }}>
            {details.birthGender === 'M' ? 'Male' : details.birthGender === 'F' ? 'Female' : ''}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {raceArray?.join(' / ')}
          </Typography>
          <Typography variant='body1' data-testid='primaryLanguageTextDisplay' sx={{ overflowWrap: 'break-word' }}>
            Speaks {details.language === null || details.language === '' ? 'English' : details.language}
          </Typography>
        </Stack>
      </Stack>
      {showMoreDetails && <Stack direction='row' spacing={1}>
        <CheckListIcon />
        <Stack direction='column' spacing={1}>
          <Stack direction='column'>
            <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Authorization</Typography>
            <Typography variant='body1' sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {details.authorizationStatus ?? '(not entered)'}
            </Typography>
          </Stack>
          {details.eligibilityFactorList.length > 0 && <Stack direction='column'>
            <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Eligibility Factors</Typography>
            <List sx={{ listStyleType: 'disc', paddingLeft: 3, py: 0 }}>
              {details.eligibilityFactorList.map((factor) => (
                <ListItem key={factor} sx={{ display: 'list-item', p: 0 }}>
                  <Typography variant='body1' sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {factor}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Stack>}
          {details.environmentalRiskList.length > 0 && <Stack direction='column'>
            <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Environmental Risks</Typography>
            <List sx={{ listStyleType: 'disc', paddingLeft: 3, py: 0 }}>
              {details.environmentalRiskList.map((risk) => (
                <ListItem key={risk} sx={{ display: 'list-item', p: 0 }}>
                  <Typography variant='body1' sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {risk}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Stack>}
          <Stack direction='column'>
            <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Additional Information</Typography>
            {!details.hasIEP && !details.hasIFSP && !details.hasEOIS && !details.isHomeless && !details.isFosterChild && !details.isOnCashAssistance && !details.isOnSSI && !details.isImmigrant && !details.hasSNAPBenefits
              ? <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>(none)</Typography>
              : <List sx={{ listStyleType: 'disc', paddingLeft: 3, py: 0 }}>
                {details.captureIEP && details.hasIEP && <ListItem sx={{ display: 'list-item', p: 0 }}>Has an IEP</ListItem>}
                {details.captureIFSP && details.hasIFSP && <ListItem sx={{ display: 'list-item', p: 0 }}>Has an IFSP</ListItem>}
                {details.captureEOIS && details.hasEOIS && <ListItem sx={{ display: 'list-item', p: 0 }}>Has an EOIS</ListItem>}
                {details.hasSNAPBenefits && <ListItem sx={{ display: 'list-item', p: 0 }}>Has SNAP Benefits</ListItem>}
                {details.isOnCashAssistance && <ListItem sx={{ display: 'list-item', p: 0 }}>Cash Assistance</ListItem>}
                {details.isFosterChild && <ListItem sx={{ display: 'list-item', p: 0 }}>Foster Child</ListItem>}
                {details.isOnSSI && <ListItem sx={{ display: 'list-item', p: 0 }}>SSI (Supplemental Security Income)</ListItem>}
                {details.isImmigrant && <ListItem sx={{ display: 'list-item', p: 0 }}>Immigrant</ListItem>}
              </List>
            }
          </Stack>
        </Stack>
      </Stack>}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button data-testid='detail-toggle' sx={{ textTransform: 'none', textDecoration: 'underline', color: 'black', px: 4 }} onClick={toggleMoreDetails}>{showMoreDetails ? 'Hide Details' : 'Show More Details'}</Button>
      </Box>
      {!details.readOnly && props.onEdit != null && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button data-testid='editChild' color='primary' variant='contained' sx={{ textTransform: 'none', px: 4 }} onClick={props.onEdit}>Edit Child Information</Button>
      </Box>}
      {(props.showLinkToFullDetails ?? false) && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button data-testid='gotoChild' sx={{ textTransform: 'none', textDecoration: 'underline', color: 'black' }} onClick={redirectToDetails}>See Full Child Record</Button>
      </Box>}
    </Stack>
  </Box>
}
